import React from "react";
import {
  GuidedInterviewPrepRecord,
  GuidedInterviewPrepTopicSection,
} from "../services/jobApplicationService";
import { getOutputModeLabel, getPrepTypeLabel } from "./interviewPrepOptions";
import "./InterviewPrepSessionDisplay.css";

interface InterviewPrepSessionDisplayProps {
  prep: GuidedInterviewPrepRecord | null;
  onBack?: () => void;
  onCreateAnother?: () => void;
  onEditRequest?: () => void;
}

/** Convert snake_case or camelCase topic IDs to Title Case for display.
 *  e.g. "motivation_for_role" → "Motivation For Role"
 *       "aiMlDepth"           → "Ai Ml Depth"
 */
const formatTopicName = (raw: string): string =>
  raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const renderList = (items?: string[]) => {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <ul className="guided-prep-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const renderTopicSection = (
  section: GuidedInterviewPrepTopicSection,
  index: number,
) => {
  const questionCount =
    section.question_count || section.questions?.length || 0;

  return (
    <article
      className="guided-prep-topic-card"
      key={`${section.topic || section.label || index}`}
    >
      <div className="guided-prep-topic-card-header">
        <div>
          <h5>
            {section.label
              ? section.label
              : section.topic
                ? formatTopicName(section.topic)
                : `Topic ${index + 1}`}
          </h5>
          {section.summary && <p>{section.summary}</p>}
        </div>
        <span className="guided-prep-topic-count">
          {questionCount > 0 ? `${questionCount} questions` : "Topic section"}
        </span>
      </div>
      {section.description && (
        <p className="guided-prep-muted">{section.description}</p>
      )}
      {section.key_points &&
        section.key_points.length > 0 &&
        renderList(section.key_points)}
      {section.questions && section.questions.length > 0 && (
        <ul className="guided-prep-list">
          {section.questions.map((question, questionIndex) => (
            <li key={`${section.topic || index}-${questionIndex}`}>
              {question}
            </li>
          ))}
        </ul>
      )}
      {section.research_notes && section.research_notes.length > 0 && (
        <p className="guided-prep-muted">{section.research_notes.join(" ")}</p>
      )}
    </article>
  );
};

export const InterviewPrepSessionDisplay: React.FC<
  InterviewPrepSessionDisplayProps
> = ({ prep, onBack, onCreateAnother, onEditRequest }) => {
  if (!prep) {
    return null;
  }

  const topicSections = prep.result?.topic_sections || prep.topics || [];

  if (prep.status === "processing") {
    const waitMessage =
      prep.message ||
      (prep.estimated_wait_seconds
        ? `Estimated wait: about ${Math.max(1, Math.round(prep.estimated_wait_seconds / 60))} minute(s).`
        : "This usually takes a few moments.");

    return (
      <div className="guided-prep-state">
        <div className="guided-prep-spinner" />
        <h3>Generating Interview Prep</h3>
        <p className="guided-prep-state-message">
          Building a job-specific prep artifact for{" "}
          {prep.position || "this role"}
          {prep.company ? ` at ${prep.company}` : ""}. {waitMessage}
        </p>
        {typeof prep.completed_topic_count === "number" &&
          typeof prep.topic_count === "number" &&
          prep.topic_count > 0 && (
            <p className="guided-prep-state-progress">
              Progress: {prep.completed_topic_count} of {prep.topic_count} topic
              sections completed.
            </p>
          )}
        {topicSections.length > 0 && (
          <div className="guided-prep-topic-grid">
            {topicSections.map((section, index) =>
              renderTopicSection(section, index),
            )}
          </div>
        )}
      </div>
    );
  }

  if (prep.status === "failed") {
    return (
      <div className="guided-prep-state guided-prep-state-error">
        <h3>Interview Prep Failed</h3>
        <p>{prep.error || "The prep could not be generated."}</p>
        {onCreateAnother && (
          <button
            type="button"
            className="guided-prep-toolbar-button guided-prep-toolbar-button-primary"
            onClick={onCreateAnother}
          >
            Create Another Prep
          </button>
        )}
        {onEditRequest && (
          <button
            type="button"
            className="guided-prep-toolbar-button"
            onClick={onEditRequest}
          >
            Edit Request
          </button>
        )}
      </div>
    );
  }

  const result = prep.result;
  const qualityCheck = result?.preparation_quality_check;
  const resumeSourceLabel =
    prep.resume_source_label ||
    prep.materials_summary?.resume_source_label ||
    result?.materials_summary?.resume_source_label;

  return (
    <div className="guided-prep-display">
      <div className="guided-prep-toolbar">
        {onBack && (
          <button
            type="button"
            className="guided-prep-toolbar-button"
            onClick={onBack}
          >
            Back
          </button>
        )}
        {onCreateAnother && (
          <button
            type="button"
            className="guided-prep-toolbar-button guided-prep-toolbar-button-primary"
            onClick={onCreateAnother}
          >
            Create Another Prep
          </button>
        )}
        {onEditRequest && (
          <button
            type="button"
            className="guided-prep-toolbar-button"
            onClick={onEditRequest}
          >
            Edit Topics
          </button>
        )}
      </div>

      <header className="guided-prep-header-card">
        <div className="guided-prep-badges">
          <span className="guided-prep-badge">
            {getPrepTypeLabel(prep.prep_type)}
          </span>
          <span className="guided-prep-badge">
            {getOutputModeLabel(prep.output_mode)}
          </span>
        </div>
        <h3>{prep.title || result?.title || "Interview Prep"}</h3>
        {prep.short_description && <p>{prep.short_description}</p>}
        {resumeSourceLabel && (
          <p className="guided-prep-muted">
            Resume source: {resumeSourceLabel}
          </p>
        )}
      </header>

      {result?.executive_summary && (
        <section className="guided-prep-section">
          <h4>Executive Summary</h4>
          <p className="guided-prep-paragraph">{result.executive_summary}</p>
        </section>
      )}

      {topicSections.length > 0 && (
        <section className="guided-prep-section">
          <h4>Topic Sections</h4>
          <div className="guided-prep-topic-grid">
            {topicSections.map((section, index) =>
              renderTopicSection(section, index),
            )}
          </div>
        </section>
      )}

      {qualityCheck && (
        <section className="guided-prep-section">
          <h4>Preparation Quality Check</h4>
          <div className="guided-prep-grid">
            {qualityCheck.evidence_boundary && (
              <article className="guided-prep-card">
                <h5>Evidence Boundary</h5>
                <p>{qualityCheck.evidence_boundary}</p>
              </article>
            )}
            {qualityCheck.ambiguity_check && (
              <article className="guided-prep-card">
                <h5>Ambiguity Check</h5>
                <p>{qualityCheck.ambiguity_check}</p>
              </article>
            )}
            {qualityCheck.scope_check && (
              <article className="guided-prep-card">
                <h5>Scope Check</h5>
                <p>{qualityCheck.scope_check}</p>
              </article>
            )}
            {qualityCheck.remediation && (
              <article className="guided-prep-card">
                <h5>Remediation</h5>
                <p>{qualityCheck.remediation}</p>
              </article>
            )}
            {qualityCheck.confidence && (
              <article className="guided-prep-card">
                <h5>Confidence</h5>
                <p>{qualityCheck.confidence}</p>
              </article>
            )}
          </div>
        </section>
      )}

      <section className="guided-prep-section">
        <div className="guided-prep-grid">
          {result?.likely_interview_loop &&
            result.likely_interview_loop.length > 0 && (
              <article className="guided-prep-card">
                <h4>Likely Interview Loop</h4>
                {renderList(result.likely_interview_loop)}
              </article>
            )}
          {result?.risk_areas && result.risk_areas.length > 0 && (
            <article className="guided-prep-card">
              <h4>Risk Areas</h4>
              {renderList(result.risk_areas)}
            </article>
          )}
        </div>
      </section>

      {result?.competency_map && result.competency_map.length > 0 && (
        <section className="guided-prep-section">
          <h4>Competency Map</h4>
          <div className="guided-prep-grid">
            {result.competency_map.map((item, index) => (
              <article
                className="guided-prep-card"
                key={`${item.competency}-${index}`}
              >
                <h5>{item.competency || "Competency"}</h5>
                {item.why_it_matters && <p>{item.why_it_matters}</p>}
                {item.signals_to_show && (
                  <p className="guided-prep-muted">{item.signals_to_show}</p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {result?.role_breakdown && result.role_breakdown.length > 0 && (
        <section className="guided-prep-section">
          <h4>Role Breakdown and Pressure Points</h4>
          <div className="guided-prep-grid">
            {result.role_breakdown.map((item, index) => (
              <article
                className="guided-prep-card"
                key={`${item.domain}-${index}`}
              >
                <h5>{item.domain || "Role domain"}</h5>
                {item.pressure_points && <p>{item.pressure_points}</p>}
                {item.confidence && (
                  <p className="guided-prep-muted">
                    Confidence: {item.confidence}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {result?.advanced_interviewer_insights &&
        result.advanced_interviewer_insights.length > 0 && (
          <section className="guided-prep-section">
            <h4>Advanced Interviewer Insights</h4>
            <div className="guided-prep-grid">
              {result.advanced_interviewer_insights.map((item, index) => (
                <article
                  className="guided-prep-card"
                  key={`${item.insight}-${index}`}
                >
                  <h5>{item.category || "Insight"}</h5>
                  {item.insight && <p>{item.insight}</p>}
                  {(item.label || item.confidence) && (
                    <p className="guided-prep-muted">
                      {[item.label, item.confidence]
                        .filter(Boolean)
                        .join(" | ")}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

      {result?.question_bank && result.question_bank.length > 0 && (
        <section className="guided-prep-section">
          <h4>Hardest Likely Questions</h4>
          <div className="guided-prep-question-list">
            {result.question_bank.map((question, index) => (
              <article
                className="guided-prep-question-card"
                key={`${question.question}-${index}`}
              >
                <div className="guided-prep-question-number">Q{index + 1}</div>
                <h5>{question.question}</h5>
                {question.why_it_is_likely && (
                  <p>
                    <strong>Why It Is Likely:</strong>{" "}
                    {question.why_it_is_likely}
                  </p>
                )}
                {question.jd_or_background_trigger && (
                  <p>
                    <strong>Trigger:</strong>{" "}
                    {question.jd_or_background_trigger}
                  </p>
                )}
                {question.what_is_being_tested && (
                  <p>
                    <strong>What Is Being Tested:</strong>{" "}
                    {question.what_is_being_tested}
                  </p>
                )}
                {question.answer_30_second && (
                  <p>
                    <strong>30-Second Answer:</strong>{" "}
                    {question.answer_30_second}
                  </p>
                )}
                {question.answer_90_second && (
                  <p>
                    <strong>90-Second Answer:</strong>{" "}
                    {question.answer_90_second}
                  </p>
                )}
                {question.strong_answer && (
                  <p>
                    <strong>Strong Answer:</strong> {question.strong_answer}
                  </p>
                )}
                {question.why_it_works && (
                  <p>
                    <strong>Why It Works:</strong> {question.why_it_works}
                  </p>
                )}
                {question.weak_answer_pattern && (
                  <p>
                    <strong>Weak Answer Pattern:</strong>{" "}
                    {question.weak_answer_pattern}
                  </p>
                )}
                {question.top_one_percent_answer_difference && (
                  <p>
                    <strong>Top 1% Answer Difference:</strong>{" "}
                    {question.top_one_percent_answer_difference}
                  </p>
                )}
                {question.deep_follow_up && (
                  <p>
                    <strong>Deep Follow-Up:</strong> {question.deep_follow_up}
                  </p>
                )}
                {(question.likely_follow_up_pushback ||
                  question.interviewer_pushback) && (
                  <p>
                    <strong>Likely Follow-Up Pushback:</strong>{" "}
                    {question.likely_follow_up_pushback ||
                      question.interviewer_pushback}
                  </p>
                )}
                {question.best_recovery_answer && (
                  <p>
                    <strong>Best Recovery Answer:</strong>{" "}
                    {question.best_recovery_answer}
                  </p>
                )}
                {question.recovery_strategy && (
                  <p>
                    <strong>Recovery Strategy:</strong>{" "}
                    {question.recovery_strategy}
                  </p>
                )}
                {question.answer_structure && (
                  <p>
                    <strong>Answer Structure:</strong>{" "}
                    {question.answer_structure}
                  </p>
                )}
                {question.likely_follow_ups &&
                  question.likely_follow_ups.length > 0 && (
                    <>
                      <p>
                        <strong>Likely Follow-Ups</strong>
                      </p>
                      {renderList(question.likely_follow_ups)}
                    </>
                  )}
                {question.trust_signals &&
                  question.trust_signals.length > 0 && (
                    <>
                      <p>
                        <strong>Trust Signals</strong>
                      </p>
                      {renderList(question.trust_signals)}
                    </>
                  )}
                {question.doubt_signals &&
                  question.doubt_signals.length > 0 && (
                    <>
                      <p>
                        <strong>Doubt Signals</strong>
                      </p>
                      {renderList(question.doubt_signals)}
                    </>
                  )}
                {question.metrics_or_evidence_to_include &&
                  question.metrics_or_evidence_to_include.length > 0 && (
                    <>
                      <p>
                        <strong>Metrics or Evidence to Include</strong>
                      </p>
                      {renderList(question.metrics_or_evidence_to_include)}
                    </>
                  )}
                {question.traps_failure_modes &&
                  question.traps_failure_modes.length > 0 && (
                    <>
                      <p>
                        <strong>Traps and Failure Modes</strong>
                      </p>
                      {renderList(question.traps_failure_modes)}
                    </>
                  )}
                {question.background_evidence_to_use &&
                  question.background_evidence_to_use.length > 0 && (
                    <>
                      <p>
                        <strong>Background Evidence to Use</strong>
                      </p>
                      {renderList(question.background_evidence_to_use)}
                    </>
                  )}
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="guided-prep-section">
        <div className="guided-prep-grid">
          {result?.questions_to_ask_interviewer &&
            result.questions_to_ask_interviewer.length > 0 && (
              <article className="guided-prep-card">
                <h4>Questions To Ask The Interviewer</h4>
                {renderList(result.questions_to_ask_interviewer)}
              </article>
            )}
          {result?.brutal_gap_analysis &&
            result.brutal_gap_analysis.length > 0 && (
              <article className="guided-prep-card">
                <h4>Brutal Gap Analysis</h4>
                {renderList(result.brutal_gap_analysis)}
              </article>
            )}
          {result?.interview_positioning_statement && (
            <article className="guided-prep-card">
              <h4>Interview Positioning Statement</h4>
              <p>{result.interview_positioning_statement}</p>
            </article>
          )}
        </div>
      </section>
    </div>
  );
};
